import { Fragment, useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";

import {
  OrganizationService,
  type AuditLogEntry,
  type AuditLogFilters,
} from "@/services/organization.service";
import { useMembers } from "@/hooks/use-organization";
import { useOrganization } from "@/context/OrganizationContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const actionBadgeVariant = (action: string): "default" | "secondary" | "destructive" | "outline" => {
  if (action.startsWith("auth.")) return "outline";
  if (
    action.endsWith(".delete") ||
    action.endsWith("_delete") ||
    action.endsWith("_reject") ||
    action === "member.remove"
  )
    return "destructive";
  if (
    action.endsWith("_activate") ||
    action.endsWith("_approve") ||
    action.startsWith("member.") ||
    action.startsWith("invitation.") ||
    action.startsWith("organization.")
  )
    return "default";
  return "secondary";
};

/** Friendly labels for the known action set; unknown slugs render as-is. */
const ACTION_LABELS: Record<string, string> = {
  "auth.sign_in": "Signed in",
  "auth.sign_in_failed": "Failed sign-in",
  "auth.sign_out": "Signed out",
  "auth.sign_up": "Signed up",
  "auth.email_verified": "Verified email",
  "auth.password_reset_requested": "Requested password reset",
  "auth.password_reset_completed": "Completed password reset",
  "organization.create": "Created organization",
  "organization.update": "Updated organization",
  "member.role_update": "Changed member role",
  "member.permissions_update": "Changed member permissions",
  "member.remove": "Removed member",
  "invitation.create": "Invited member",
  "invitation.accept": "Accepted invitation",
  "invitation.cancel": "Cancelled invitation",
  "moderation.ruleset_create": "Created moderation ruleset",
  "moderation.ruleset_update": "Updated moderation ruleset",
  "moderation.rule_create": "Added moderation rule",
  "moderation.rule_update": "Updated moderation rule",
  "review.result_approve": "Approved moderation result",
  "review.result_reject": "Rejected moderation result",
  "review.feedback_accept": "Accepted moderation feedback",
  "review.feedback_reject": "Rejected moderation feedback",
  "review.rerun": "Requested moderation rerun",
  "catalogue.create": "Created catalogue",
  "catalogue.update": "Updated catalogue",
  "catalogue.delete": "Deleted catalogue",
  "catalogue.restore": "Restored catalogue",
  "catalogue.apply_category_template": "Applied category template",
  "category.create": "Created taxonomy category",
  "category.update": "Updated taxonomy category",
  "category.delete": "Deleted taxonomy category",
  "category.attributes_update": "Updated attribute schema",
  "category.attribute_translations_update": "Updated attribute translations",
  "category.title_template_update": "Updated title template",
  "upload.create": "Uploaded file",
};

const actionLabel = (action: string): string => ACTION_LABELS[action] ?? action;

type PayloadObject = Record<string, unknown>;

const isObject = (value: unknown): value is PayloadObject =>
  !!value && typeof value === "object" && !Array.isArray(value);

const formatValue = (value: unknown): string => {
  if (value === undefined || value === null) return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

/** "status: draft → active, severity: low → high" for before/after pairs. */
const diffLine = (before: PayloadObject, after: PayloadObject): string | null => {
  const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)])).filter(
    (key) => JSON.stringify(before[key]) !== JSON.stringify(after[key])
  );
  if (keys.length === 0) return null;
  return keys.map((key) => `${key}: ${formatValue(before[key])} → ${formatValue(after[key])}`).join(", ");
};

/**
 * Actions whose payloads are composed server-side (explicit audit writes).
 * Generic-middleware entries nest the raw client body under `request`, but
 * historical rows stored it at the top level — so summary/member/before/after
 * are only trusted for these namespaces, or a client-sent "summary" body key
 * could masquerade as an audit narrative.
 */
const TRUSTED_PAYLOAD_PREFIXES = [
  "auth.",
  "organization.",
  "member.",
  "invitation.",
  "moderation.",
  "review.",
];

const TRUSTED_CONFIGURATION_ACTIONS = new Set([
  "catalogue.apply_category_template",
  "category.create",
  "category.update",
  "category.delete",
  "category.attributes_update",
  "category.attribute_translations_update",
  "category.title_template_update",
]);

const hasServerComposedPayload = (action: string): boolean =>
  TRUSTED_PAYLOAD_PREFIXES.some((prefix) => action.startsWith(prefix)) ||
  TRUSTED_CONFIGURATION_ACTIONS.has(action);

/**
 * One human-readable line for the Details column. New entries (moderation,
 * review) carry a server-composed `payload.summary`; older org entries are
 * summarized client-side from their {member, before, after} payloads.
 */
const summarizeEntry = (entry: AuditLogEntry): string | null => {
  const payload = entry.payload;
  if (!isObject(payload)) return null;

  if (hasServerComposedPayload(entry.action)) {
    if (typeof payload.summary === "string" && payload.summary) return payload.summary;

    const parts: string[] = [];
    if (isObject(payload.member) && typeof payload.member.email === "string") {
      parts.push(payload.member.email);
    }
    if (isObject(payload.before) && isObject(payload.after)) {
      const diff = diffLine(payload.before, payload.after);
      if (diff) parts.push(diff);
    }
    if (parts.length > 0) return parts.join(" — ");
  }

  if (typeof payload.catalogueId === "string") return `catalogue ${payload.catalogueId}`;
  return null;
};

const hasExpandableDetails = (entry: AuditLogEntry): boolean => isObject(entry.payload);

export function AuditLogTable() {
  const { can } = useOrganization();
  const { members } = useMembers(can("team"));

  const [actorUserId, setActorUserId] = useState<string>("all");
  const [actionPrefix, setActionPrefix] = useState("");
  const [items, setItems] = useState<AuditLogEntry[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const filters: AuditLogFilters = {
    ...(actorUserId !== "all" ? { actorUserId } : {}),
    ...(actionPrefix.trim() ? { action: actionPrefix.trim() } : {}),
  };

  const load = async (cursor?: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const page = await OrganizationService.getAuditLogs(filters, cursor);
      setItems((current) => (cursor ? [...current, ...page.items] : page.items));
      setNextCursor(page.nextCursor);
    } catch (err: any) {
      setError(err?.message || "Failed to load the audit log.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setExpandedId(null);
    void load();
  }, [actorUserId, actionPrefix]);

  return (
    <div className="space-y-4 px-6 py-6">
      <div className="flex flex-wrap items-end gap-4">
        <div className="space-y-1">
          <Label htmlFor="audit-actor" className="text-xs">
            Member
          </Label>
          <Select value={actorUserId} onValueChange={setActorUserId}>
            <SelectTrigger id="audit-actor" className="w-56">
              <SelectValue placeholder="All members" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All members</SelectItem>
              {members.map((member) => (
                <SelectItem key={member.user.id} value={member.user.id}>
                  {member.user.name || member.user.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="audit-action" className="text-xs">
            Action prefix
          </Label>
          <Input
            id="audit-action"
            className="w-56"
            placeholder='e.g. "moderation." or "auth."'
            value={actionPrefix}
            onChange={(e) => setActionPrefix(e.target.value)}
          />
        </div>
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-8" />
            <TableHead>Time</TableHead>
            <TableHead>Member</TableHead>
            <TableHead>Action</TableHead>
            <TableHead>Details</TableHead>
            <TableHead>Resource</TableHead>
            <TableHead>IP</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading && items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="h-24 text-center">
                <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
              </TableCell>
            </TableRow>
          ) : items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                No audit entries match the current filters.
              </TableCell>
            </TableRow>
          ) : (
            items.map((entry) => {
              const expandable = hasExpandableDetails(entry);
              const isExpanded = expandedId === entry.id;
              const summary = summarizeEntry(entry);
              return (
                <Fragment key={entry.id}>
                  <TableRow
                    className={expandable ? "cursor-pointer" : undefined}
                    onClick={
                      expandable ? () => setExpandedId(isExpanded ? null : entry.id) : undefined
                    }
                  >
                    <TableCell className="w-8 px-2 text-muted-foreground">
                      {expandable &&
                        (isExpanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        ))}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                      {new Date(entry.createdAt).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="text-sm">{entry.actorName || "—"}</span>
                        <span className="text-xs text-muted-foreground">{entry.actorEmail}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={actionBadgeVariant(entry.action)}
                        className="text-xs"
                        title={entry.action}
                      >
                        {actionLabel(entry.action)}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-[360px]">
                      <span className="line-clamp-2 text-xs text-muted-foreground" title={summary ?? undefined}>
                        {summary ?? "—"}
                      </span>
                    </TableCell>
                    <TableCell className="max-w-[220px]">
                      <div className="flex flex-col">
                        <span className="text-xs">{entry.resourceType ?? "—"}</span>
                        {entry.resourceId && (
                          <span className="truncate font-mono text-[10px] text-muted-foreground">
                            {entry.resourceId}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {entry.ipAddress ?? "—"}
                    </TableCell>
                  </TableRow>
                  {isExpanded && (
                    <TableRow className="bg-muted/30 hover:bg-muted/30">
                      <TableCell colSpan={7} className="px-10 py-3">
                        <div className="space-y-2">
                          {(entry.method || entry.path) && (
                            <p className="font-mono text-xs text-muted-foreground">
                              {entry.method} {entry.path}
                              {entry.status != null && ` → ${entry.status}`}
                            </p>
                          )}
                          <pre className="max-h-80 overflow-auto rounded-md bg-muted p-3 font-mono text-xs">
                            {JSON.stringify(entry.payload, null, 2)}
                          </pre>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })
          )}
        </TableBody>
      </Table>

      {nextCursor && (
        <div className="flex justify-center">
          <Button variant="outline" onClick={() => load(nextCursor)} disabled={isLoading}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Load more
          </Button>
        </div>
      )}
    </div>
  );
}
